import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useNavigate, useTranslation } from '@ufc/core';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
} from '@ufc/uikit';

import {
  clearStoredLanguage,
  getStoredLanguage,
  LANGUAGE_INACTIVITY_TIMEOUT_MS,
  LANGUAGE_OPTIONS,
  persistLanguage,
  type SupportedLanguage,
} from '@/lib/language-preferences';

interface LanguageSessionProviderProps {
  children: ReactNode;
}

type LanguagePromptReason = 'initial' | 'expired';

const ACTIVITY_EVENTS = [
  'pointerdown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
] as const;

function getPromptDescriptionKey(reason: LanguagePromptReason): string {
  if (reason === 'expired') return 'languagePrompt.expiredDescription';
  return 'languagePrompt.description';
}

function getPromptDescriptionFallback(reason: LanguagePromptReason): string {
  if (reason === 'expired') {
    return 'Your session expired due to inactivity. Select a language to continue again.';
  }

  return 'Select a language to continue.';
}

function getLanguageButtonStyles(isActive: boolean): string {
  return cn(
    'h-auto min-h-16 w-full justify-between rounded-2xl border-2 px-4 py-4 text-left',
    'hover:border-primary/60 hover:bg-primary/5',
    isActive && 'border-primary bg-primary/10',
  );
}

export function LanguageSessionProvider({
  children,
}: LanguageSessionProviderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const inactivityTimerRef = useRef<number | null>(null);
  const [promptReason, setPromptReason] =
    useState<LanguagePromptReason>('initial');
  const [requiresLanguageSelection, setRequiresLanguageSelection] = useState(
    () => getStoredLanguage() === null,
  );
  const [sessionResetKey, setSessionResetKey] = useState(0);

  const clearInactivityTimer = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (inactivityTimerRef.current === null) return;

    window.clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = null;
  }, []);

  const openLanguagePrompt = useCallback(
    (reason: LanguagePromptReason) => {
      clearInactivityTimer();
      setPromptReason(reason);
      setRequiresLanguageSelection(true);
    },
    [clearInactivityTimer],
  );

  const handleInactivityTimeout = useCallback(() => {
    clearStoredLanguage();
    setSessionResetKey((currentKey) => currentKey + 1);
    openLanguagePrompt('expired');
    navigate({ to: '/', replace: true });
  }, [navigate, openLanguagePrompt]);

  const startInactivityTimer = useCallback(() => {
    if (typeof window === 'undefined') return;

    clearInactivityTimer();

    if (requiresLanguageSelection) return;

    inactivityTimerRef.current = window.setTimeout(
      handleInactivityTimeout,
      LANGUAGE_INACTIVITY_TIMEOUT_MS,
    );
  }, [
    clearInactivityTimer,
    handleInactivityTimeout,
    requiresLanguageSelection,
  ]);

  const handleUserActivity = useCallback(() => {
    startInactivityTimer();
  }, [startInactivityTimer]);

  const handleLanguageSelect = useCallback(
    (language: SupportedLanguage) => {
      persistLanguage(language);
      void i18n.changeLanguage(language);
      setPromptReason('initial');
      setRequiresLanguageSelection(false);
    },
    [i18n],
  );

  const preventDialogClose = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    const handleForceTimeout = () => {
      handleInactivityTimeout();
    };

    window.addEventListener('session-timeout', handleForceTimeout);
    return () => {
      window.removeEventListener('session-timeout', handleForceTimeout);
    };
  }, [handleInactivityTimeout]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (requiresLanguageSelection) {
      clearInactivityTimer();
      return undefined;
    }

    startInactivityTimer();

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity);
    });

    return () => {
      clearInactivityTimer();

      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
    };
  }, [
    clearInactivityTimer,
    handleUserActivity,
    requiresLanguageSelection,
    startInactivityTimer,
  ]);

  return (
    <>
      <Fragment key={sessionResetKey}>{children}</Fragment>

      <Dialog open={requiresLanguageSelection}>
        <DialogContent
          showCloseButton={false}
          className="max-w-[calc(100%-2rem)] rounded-3xl p-6 sm:max-w-md sm:p-8"
          onEscapeKeyDown={preventDialogClose}
          onInteractOutside={preventDialogClose}
          onPointerDownOutside={preventDialogClose}
        >
          <DialogHeader className="space-y-3 text-center sm:text-center">
            <DialogTitle className="text-2xl font-bold">
              {t('languagePrompt.title', 'Select language')}
            </DialogTitle>
            <DialogDescription className="text-sm leading-6">
              {t(
                getPromptDescriptionKey(promptReason),
                getPromptDescriptionFallback(promptReason),
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 pt-2">
            {LANGUAGE_OPTIONS.map((language) => (
              <Button
                key={language.code}
                type="button"
                variant="outline"
                className={getLanguageButtonStyles(
                  i18n.language === language.code,
                )}
                onClick={() => handleLanguageSelect(language.code)}
              >
                <span className="flex flex-col items-start">
                  <span className="text-base font-semibold">
                    {language.nativeLabel}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {language.englishLabel}
                  </span>
                </span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
